import { Box, PopoverPosition, useTheme } from "@mui/material";
import * as React from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import {
  ExpandableCopyText,
  RobotoCheckbox,
  RobotoErrorAlertDialog,
  RobotoTableCell,
} from "@/shared/components";
import { IngestionLabelBadge } from "@/shared/components/files/IngestionLabelBadge";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal/RobotoPrincipalUnderlineLink.tsx";
import { FormattedDateTime } from "@/shared/datetime";
import { type FileRecord } from "@/shared/domain/files";
import {
  basename,
  FileNode,
  FileSystemNode,
  isDirectory,
  isFile,
} from "@/shared/domain/files/filesystem";
import { FileSystemNodeType } from "@/shared/domain/files/filesystem/fileSystemNode";
import { assetSourceToIngest } from "@/shared/domain/urdf";
import { useLazyAPICall } from "@/shared/services/apiHooks";
import { formatByteSize } from "@/shared/text";
import {
  APIServiceError,
  FileSignedUrlResponse,
  fileSignedUrlEndpoint,
} from "@/types";

import { ActionCell } from "./ActionCell";
import { ActionMenu } from "./ActionMenu";
import styles from "./DatasetFileRow.module.css";
import { ExpandRowIcon } from "./ExpandRowIcon";

interface DatasetFileRowProps {
  isChecked: boolean;
  isRowExpanded: boolean;
  row: FileSystemNode;
  goToFile: () => void;
  handleActionConfirmation: (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => void;
  /**
   * Opens the ingest wizard for a robot description. Owned by the browser.
   *
   * Optional, and absent is the whole of the release gate: the browser
   * withholds the handler when `release-3d-assets` is off, and a robot
   * description then opens the ordinary file view like anything else.
   */
  onIngestAsAsset?: (file: FileRecord) => void;
  /** Whether a file's name has its ingestion label beside it (`release-ingestion-labels`). */
  showIngestionLabel: boolean;
  handleDeleteRow: (row: FileSystemNode) => void;
  handleRowDoubleClick: (row: FileSystemNode) => void;
  handleRenameNode: () => void;
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  toggleCollapse: () => void;
}

export const DatasetFileRow: React.FC<DatasetFileRowProps> = ({
  isChecked,
  isRowExpanded,
  row,
  goToFile,
  handleActionConfirmation,
  onIngestAsAsset,
  showIngestionLabel,
  handleDeleteRow,
  handleRowDoubleClick,
  handleRenameNode,
  handleSelectRows,
  onMenuOpen,
  onMenuClose,
  toggleCollapse,
}) => {
  const theme = useTheme();

  const { files: fileService } = useDomainServices();

  const { currentOrganization } = useAuth();
  const [alertOpen, setAlertOpen] = React.useState<boolean>(false);
  const [lastError, setLastError] = React.useState<APIServiceError | undefined>(
    undefined,
  );

  const [anchorPos, setAnchorPos] = React.useState<PopoverPosition>();

  const handlePopoverMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorPos({ top: event.clientY, left: event.clientX });
    onMenuOpen();
  };

  const { initiateRequest: initiateSignedUrlRequest } =
    useLazyAPICall<FileSignedUrlResponse>();

  const handleError = (error: APIServiceError | undefined) => {
    setLastError(error);
    setAlertOpen(true);
  };

  const fetchSignedUrl = async (row: FileNode, forDownload?: boolean) => {
    const file = row.file;
    if (!file) {
      return;
    }

    const { data, error } = await initiateSignedUrlRequest({
      endpoint: fileSignedUrlEndpoint,
      method: "GET",
      orgId: currentOrganization?.org_id,
      queryParams: fileService.getSignedUrlParams(file, forDownload),
      pathParams: { fileId: file.file_id },
    });

    if (!error && data?.data) {
      return data.data.url;
    }

    if (error) {
      handleError(error);
    }
  };

  const rowIsFileType = row?.type === FileSystemNodeType.File;

  const itemDoubleClicked = () => {
    // Opening an ingestable source means seeing the model, not the bytes, so
    // it goes to the ingest wizard rather than the file view — the same choice
    // the row menu's View item makes. With no wizard wired up it falls through
    // to the file view, which is what every other file does.
    const sourceToIngest = assetSourceToIngest(row);
    if (sourceToIngest && onIngestAsAsset) {
      onIngestAsAsset(sourceToIngest);
    } else if (row.type === FileSystemNodeType.File) {
      goToFile();
    } else if (row.type === FileSystemNodeType.Directory) {
      handleRowDoubleClick(row);
    }
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    handlePopoverMenu(event);
  };

  return (
    <>
      <RobotoErrorAlertDialog
        open={alertOpen}
        setOpen={setAlertOpen}
        error={lastError}
      />
      <RobotoTableCell
        sx={{
          padding: "0px",
          width: "42.5px",
          maxWidth: "42.5px",
        }}
        onContextMenu={handleContextMenu}
      >
        <RobotoCheckbox
          sx={{
            ml: theme.spacing(1),
            display: {
              xs: "none",
              sm: "inline-flex",
            },
          }}
          checked={isChecked}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleSelectRows([row], e.target.checked);
          }}
          color="default"
          inputProps={{ "aria-label": "checkbox" }}
        />
      </RobotoTableCell>

      <RobotoTableCell
        onDoubleClick={itemDoubleClicked}
        onContextMenu={handleContextMenu}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1.5),
            userSelect: "none",
            wordBreak: "break-all",
          }}
        >
          <ExpandRowIcon
            isExpanderOpen={isRowExpanded}
            row={row}
            handleIconClick={() => {
              toggleCollapse();
            }}
          />
          <ExpandableCopyText
            text={basename(row.name)}
            trimLength={basename(row.name).length}
          />
          {isFile(row) && showIngestionLabel && (
            <IngestionLabelBadge
              file={row.file}
              className={styles.ingestionLabel}
            />
          )}
        </Box>
      </RobotoTableCell>
      <RobotoTableCell
        onDoubleClick={itemDoubleClicked}
        onContextMenu={handleContextMenu}
        sx={{
          userSelect: "none",
        }}
      >
        {isFile(row) ? <FormattedDateTime value={row.file.modified} /> : ""}
        {isDirectory(row) ? (
          <FormattedDateTime value={row.directory.modified} />
        ) : (
          ""
        )}
      </RobotoTableCell>
      <RobotoTableCell
        onDoubleClick={itemDoubleClicked}
        onContextMenu={handleContextMenu}
        sx={{
          minWidth: "10rem",
        }}
      >
        {isFile(row) && (
          <RobotoPrincipalUnderlineLink principal={row.file.created_by} icon />
        )}
      </RobotoTableCell>
      <RobotoTableCell
        onDoubleClick={itemDoubleClicked}
        onContextMenu={handleContextMenu}
        sx={{
          userSelect: "none",
        }}
      >
        {isFile(row) ? formatByteSize(row.file?.size) : ""}
      </RobotoTableCell>
      <ActionCell
        row={row}
        handleMoreClicked={(e) => {
          e.stopPropagation();
          handlePopoverMenu(e);
        }}
      />

      <ActionMenu
        row={row}
        onDeleteSuccess={() => {
          // Remove row from UI
          handleDeleteRow(row);
        }}
        anchorPos={anchorPos}
        setAnchorPos={setAnchorPos}
        handleActionConfirmation={handleActionConfirmation}
        onIngestAsAsset={onIngestAsAsset}
        fetchSignedUrl={(row) => fetchSignedUrl(row, true)}
        openContent={() => {
          goToFile();
        }}
        checked={isChecked}
        handleSelectRows={handleSelectRows}
        detailsExpandable={true}
        openDetails={toggleCollapse}
        handleRenameNode={handleRenameNode}
        rowIsFileType={rowIsFileType}
        onClose={onMenuClose}
      />
    </>
  );
};
